// src/composables/useChat.js
import {computed, nextTick, ref, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {useAuthStore} from "@/store/authStore.js";
import {useChatStore, CHAT_ENABLED as CHAT_FLAG} from "@/modules/profile/store/chatStore.js";
import {HEADER_HEIGHT} from "@/constants.js";
import {useChatScroll} from "@/modules/profile/composables/useChatScroll.js";
import {useChatObserver} from "@/modules/profile/composables/useChatObserver.js";
import {useUnreadMessagesStore} from "@/modules/profile/store/unreadMessagesStore.js";
import {debounce} from "lodash-es";
import api from "@/core/services/axios.js";
import notifier from "@/core/utils/notifier.js";
import {TOAST_OPTIONS} from "@/constants.js";

export function useChat({isMobile = ref(false), headerHeight = ref(HEADER_HEIGHT), showChatList = true, currentUserId = ref(null), driveRouting = true} = {}) {
    const router = useRouter();
    const route = useRoute();
    const authStore = useAuthStore();
    const chatStore = useChatStore();

    const newMessage = ref('');
    const isLoading = ref(false);

    const messages = computed(() => chatStore.messages);
    const currentUser = computed(() => chatStore.currentUser);
    const currentChat = computed(() => chatStore.currentChat);
    const wsState = computed(() => chatStore.wsState);
    const chats = computed(() => chatStore.chats);
    const messagesContainer = ref(null);

    const unreadMessagesStore = useUnreadMessagesStore();

    const {
        setupObserver,
        observeMessages,
        disconnectObserver,
    } = useChatObserver({
        messagesContainer: messagesContainer,
        markMessagesAsRead: chatStore.markMessagesAsRead,
    });

    // Методы Scroll
    const {
        scrollToFirstUnread,
        loadUntilFirstUnread,
        handleScroll
    } = useChatScroll({
        messagesContainer: messagesContainer,
        chatStore: chatStore,
        currentUser: currentUser,
        headerHeight: headerHeight,
        isLoading: isLoading,
        observeMessages: observeMessages,
    });

    // Общие методы
    const sendMessage = async () => {
        const msg = newMessage.value.trim();
        if (msg) {
            if (!authStore.requireConfirmedEmail()) return;

            await chatStore.sendMessage(msg);
            newMessage.value = '';

            // Скроллим вниз после отправки
            await nextTick();
            if (messagesContainer.value) {
                messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
            }
        }
    };

    // Отправка первого сообщения для создания нового чата
    const sendFirstMessage = async (listingId, text) => {
        const msg = text.trim();
        if (!msg) return;
        if (!CHAT_FLAG) {
            notifier.error('Чат временно отключён');
            return;
        }
        if (!authStore.requireConfirmedEmail()) return;

        isLoading.value = true;
        try {
            const response = await api.post('/chats/messages', {
                listing_id: listingId,
                text: msg,
            });

            const chatId = response?.data?.chat_id;
            if (chatId) {
                // Обновляем список чатов
                await chatStore.fetchChats();

                // Переходим на страницу нового чата
                await router.replace({name: 'ChatDetailPage', params: {id: chatId}});
                await handleChatByChatId(chatId);

                // Скроллим вниз
                await nextTick();
                if (messagesContainer.value) {
                    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
                }
            }
        } catch (error) {
            const errorMsg = error?.response?.data?.error || error?.response?.data?.message;
            if (errorMsg === 'INVALID_PARTICIPANT') {
                notifier.error(TOAST_OPTIONS.error, 'Вы не можете написать себе. Это ваше объявление.');
            } else {
                console.error('[sendFirstMessage] Error:', error);
                notifier.error(TOAST_OPTIONS.error, 'Не удалось отправить сообщение');
            }
        } finally {
            isLoading.value = false;
        }
    };

    const parseNumber = (number) => {
        return (Number(number) ? (Number.isNaN(number) ? 0 : number) : 0);
    };

    const selectChat = async (chatId) => {
        await router.push({name: 'ChatDetailPage', params: {id: chatId}});
        await handleChatByChatId(chatId);
    };

    const handleChatByListingId = async (listingId) => {
        isLoading.value = true;

        try {
            // Ищем существующий чат по listing_id
            const existingChat = chatStore.chats?.find(chat =>
                String(chat.listing_id) === String(listingId)
            );

            if (existingChat) {
                await router.replace({name: 'ChatDetailPage', params: {id: existingChat.id}});
                await handleChatByChatId(existingChat.id);
                return;
            }

            // Чата нет — переходим на страницу чата с listing_id в query
            // Пользователь сам напишет первое сообщение, чат создастся автоматически
            await router.push({name: 'ChatPage', query: {listing_id: listingId}});
        } catch (error) {
            console.error('[useChat.handleChatByListingId] Error:', error);
        } finally {
            isLoading.value = false;
        }
    };

    const handleChatByUserId = async (userId, listingId = null) => {
        isLoading.value = true;
        const existingChat = chatStore.chats?.find(chat => !chat.is_group && String(chat.partner_id) === String(userId));

        try {
            // Если есть existingChat, используем его
            if (existingChat) {
                await router.replace({name: 'ChatDetailPage', params: {id: existingChat.id}});
                await handleChatByChatId(existingChat.id);
                return;
            }

            // Если listingId есть — переходим на страницу чата с listing_id
            // Пользователь сам напишет первое сообщение
            if (listingId) {
                await router.push({name: 'ChatPage', query: {listing_id: listingId, user_id: userId}});
            } else {
                router.push({name: 'ChatPage'});
            }
        } catch (error) {
            console.error('[useChat.handleChatByUserId] Error:', error);
        } finally {
            isLoading.value = false;
        }
    };

    const handleChatByChatId = async (chatId) => {
        isLoading.value = true;
        try {
            await chatStore.selectChat(chatId);

            // unread_count сбрасывается внутри chatStore.selectChat
            updateUnreadCount();

            // Ждем загрузки сообщений и обновления DOM
            await nextTick();
            isLoading.value = false;

            // Скролл теперь обрабатывается автоматически в useChatScroll через watch на messages
        } catch (error) {
            console.error('Error handleChatByChatId:', error);
        } finally {
            isLoading.value = false;
        }
    };

    let prevCount = 0;
    watch(() => unreadMessagesStore.count, (newCount) => {
        if (newCount > prevCount && newCount > 0) {
            if (isMobile) {
                notifyNewMessage();
            } else {
                playNotificationSound();
            }
        }
        prevCount = newCount;
    });

    const updateUnreadCount = debounce(() => {
        const total = chatStore.chats?.reduce((sum, chat) => sum + (chat.unread_count > 0 ? chat.unread_count : 0), 0) || 0;
        unreadMessagesStore.set(total);
    }, 200);

    // Обрабатывает текущую цель из URL (chat_id / user_id / listing_id) —
    // отдельно от одноразового бутстрапа ниже, потому что должна отрабатывать
    // при КАЖДОМ монтировании driveRouting-экземпляра (например, каждый заход
    // на ChatPage), а не только один раз за всё время жизни вкладки.
    const processRouteTarget = async () => {
        const chat_id = route.params.id || route.query.chat_id;
        const user_id = route.query.user_id;
        const listing_id = route.query.listing_id;

        if (chat_id) {
            await handleChatByChatId(chat_id);
        } else if (user_id) {
            const existingChat = chatStore.chats?.find(chat =>
                !chat.is_group && String(chat.partner_id) === String(user_id)
            );
            if (existingChat) {
                await router.replace({name: 'ChatDetailPage', params: {id: existingChat.id}})
                await handleChatByChatId(existingChat.id);
            }
            // Если чата нет — остаёмся на странице с listing_id в query, пользователь сам напишет
        } else if (listing_id && !user_id) {
            // Если есть только listing_id, ищем существующий чат
            const existingChat = chatStore.chats?.find(chat =>
                String(chat.listing_id) === String(listing_id)
            );
            if (existingChat) {
                await router.replace({name: 'ChatDetailPage', params: {id: existingChat.id}});
                await handleChatByChatId(existingChat.id);
            }
            // Если чата нет — просто показываем страницу чатов, пользователь сам создаст чат
        }
    };

    // Хуки жизненного цикла
    const initChat = async () => {
        if (!CHAT_FLAG) {
            console.warn('[initChat] Chat feature is disabled by flag.');
            return;
        }

        if (!chatStore.initialized) {
            chatStore.initialized = true;

            chatStore.clear();

            if (!authStore.userId) {
                await authStore.initAuth();
            }

            const userId = authStore.userId;
            if (userId) {
                chatStore.setCurrentUser({id: String(userId)});
            } else {
                console.warn('[useChat.initChat] Could not determine current user ID');
            }

            // Подключаем WebSocket ДО загрузки чатов
            if (chatStore.wsState === 'disconnected' || chatStore.wsState === 'error') {
                await chatStore.connectWebSocket();
            }

            // Загружаем чаты
            await chatStore.fetchChats();

            // Обновляем счётчик при изменении списка чатов (unread_count живёт там же)
            watch(() => chatStore.chats, updateUnreadCount, {immediate: true, deep: true});
        }

        // Бутстрап (веб-сокет, список чатов) мог уже случиться в другом
        // экземпляре (DefaultLayout) до того, как открылась эта страница —
        // цель из URL при этом ещё не обработана, обрабатываем её здесь.
        if (driveRouting) {
            await processRouteTarget();
        }

        setupObserver();
    };

    const cleanup = async () => {
        await chatStore.disconnectWebSocket?.();
        disconnectObserver();
        chatStore.clear();
    };

    // Кнопка "Назад" для мобильной версии
    const backToChatList = () => {
        router.push({name: 'ChatPage'})
    }

    // URL-реактивные watcher'ы ниже переключают текущий чат по параметрам роута.
    // useChat() вызывается в двух местах: глобально в DefaultLayout.vue (только
    // для веб-сокета/счётчика непрочитанных) и в самой ChatPage.vue. Если оба
    // экземпляра регистрируют эти watcher'ы, они гоняют chatStore.selectChat/clear()
    // параллельно на одном и том же сторе — итог зависит от того, чей async-вызов
    // отработает последним (не баг конкретного места, а гонка между экземплярами).
    // driveRouting=false (DefaultLayout) отключает эту часть — реальную навигацию
    // ведёт только тот экземпляр, что реально рендерит страницу чата.
    if (driveRouting) {
        // Watcher для навигации по прямому chat_id (например, из уведомлений)
        watch(() => route.params.id, async (newId, oldId) => {
            if (newId && newId !== oldId && chatStore.initialized) {
                await handleChatByChatId(newId);
            }
        });

        // Watcher для обработки изменения user_id в URL
        watch(() => route.query.user_id, async (newUserId) => {
            if (newUserId && chatStore.initialized) {
                const listingId = route.query.listing_id;
                const existingChat = chatStore.chats?.find(chat =>
                    !chat.is_group && String(chat.partner_id) === String(newUserId)
                );
                if (existingChat) {
                    await router.replace({name: 'ChatDetailPage', params: {id: existingChat.id}});
                    await handleChatByChatId(existingChat.id);
                }
                // Если чата нет — остаёмся на странице с listing_id, пользователь сам напишет
            }
        });

        // Watcher для предзаполнения сообщения (например, при отклике на вакансию)
        watch(() => route.query.message, (msg) => {
            if (msg) {
                newMessage.value = decodeURIComponent(msg);
            }
        }, { immediate: true });

        // Сбрасываем currentChat при уходе со страницы чата
        watch(() => route.name, (newRoute, oldRoute) => {
            if (oldRoute === 'ChatDetailPage' && newRoute !== 'ChatDetailPage') {
                chatStore.clear();
            }
        });

        // Устаревший currentChat не сбрасывается при переходе на другую цель без
        // chat_id (например, «Написать» на новом профиле, пока уже открыт другой
        // чат) — иначе в шаблоне продолжает показываться старая переписка, и
        // сообщение может уйти не туда. Переходы по chat_id уже сами чистят стейт
        // через chatStore.selectChat.
        watch(() => `${route.params.id || route.query.chat_id || ''}|${route.query.user_id || ''}|${route.query.listing_id || ''}`, () => {
            if (route.params.id || route.query.chat_id) return;

            const cur = chatStore.currentChat;
            if (!cur) return;

            const targetUserId = route.query.user_id ? String(route.query.user_id) : null;
            const targetListingId = route.query.listing_id ? String(route.query.listing_id) : null;
            const matchesCurrent = targetUserId
                ? String(cur.partner_id) === targetUserId
                : (targetListingId ? String(cur.listing_id) === targetListingId : false);

            if (!matchesCurrent) {
                chatStore.clear();
            }
        });
    }

    return {
        // Реактивные данные
        newMessage,
        isLoading,
        messagesContainer,

        // Вычисляемые свойства
        messages,
        currentUser,
        currentChat,
        wsState,
        chats,

        // Методы
        sendMessage,
        sendFirstMessage,
        parseNumber,
        selectChat,
        backToChatList,
        handleScroll,
        scrollToFirstUnread,

        // Методы жизненного цикла
        initChat,
        cleanup,

        // Вспомогательные методы
        getLogoChat: chatStore.getLogoChat,

        // Настройки
        isMobile,
        headerHeight,
        showChatList
    };
}

function playNotificationSound() {
    const audio = new Audio('/notification.mp3');
    audio.play().catch(() => {});
}

function notifyNewMessage() {
    if ('vibrate' in navigator) {
        navigator.vibrate(200);
    }
    playNotificationSound();
}